import { useEffect, useState } from "react";
import api from "../../lib/api";
import { EVIDENCE_KIND_LABELS, evidenceKinds } from "../../lib/mandates";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, Search, X } from "lucide-react";

const SEARCH = {
  governance_decision: { url: "/governance/decisions", params: (q) => ({ q, view: "all", limit: 8 }) },
  records_document: { url: "/records/documents", params: (q) => ({ search: q, include_closed: true, limit: 8 }) },
};

// Justificatifs d'un mandat : des RÉFÉRENCES vers une décision de gouvernance ou un document du registre (jamais un fichier copié ici).
// On ne propose que ce que les modules activés permettent de lire ; ce que la personne ne peut pas lire n'apparaît pas dans la recherche.
export default function EvidencePicker({ value, onChange, features }) {
  const kinds = evidenceKinds(features);
  const [kind, setKind] = useState(kinds[0]?.value || "");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!kind || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(SEARCH[kind].url, { params: SEARCH[kind].params(query.trim()) });
        if (!cancelled) setResults((data.items || []).map((row) => ({ kind, ref: row.id, number: row.number, title: row.title })));
      } catch (e) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [kind, query]);

  const has = (item) => value.some((e) => e.kind === item.kind && e.ref === item.ref);
  const add = (item) => { if (!has(item)) onChange([...value, item]); setQuery(""); setResults([]); };
  const remove = (item) => onChange(value.filter((e) => !(e.kind === item.kind && e.ref === item.ref)));

  return (
    <div className="space-y-2" data-testid="evidence-picker">
      <Label>Justificatifs</Label>
      {value.length > 0 && (
        <ul className="space-y-1" data-testid="evidence-list">
          {value.map((item) => (
            <li key={`${item.kind}:${item.ref}`} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm" data-testid="evidence-item">
              <span className="min-w-0 break-words">
                <span className="text-xs text-slate-500">{EVIDENCE_KIND_LABELS[item.kind]}</span>{" "}
                {item.number ? <span className="font-medium">{item.number}</span> : null}
                {item.title ? <span className="text-slate-600"> · {item.title}</span> : null}
                {!item.number && !item.title ? <span className="text-slate-500">{item.accessible === false ? "non accessible pour vous" : "déjà joint"}</span> : null}
              </span>
              <Button type="button" variant="ghost" size="icon" aria-label="Retirer ce justificatif" onClick={() => remove(item)}><X size={14} /></Button>
            </li>
          ))}
        </ul>
      )}
      {kinds.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="evidence-unavailable">Aucun module de justificatifs n'est activé (décisions de gouvernance, registre des documents) : le mandat reste un brouillon.</p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[12rem_1fr]">
            <Select value={kind} onValueChange={(v) => { setKind(v); setResults([]); }}>
              <SelectTrigger aria-label="Type de justificatif" data-testid="select-evidence-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{kinds.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-3 text-slate-400" aria-hidden="true" />
              <Input className="pl-8 text-base md:text-sm" placeholder="Numéro ou titre (2 lettres au moins)" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="input-evidence-search" aria-label="Chercher un justificatif" />
            </div>
          </div>
          {loading && <div className="flex items-center gap-1.5 text-xs text-slate-500"><Loader2 size={12} className="animate-spin" /> Recherche…</div>}
          {!loading && query.trim().length >= 2 && results.length === 0 && <p className="text-xs text-slate-500" data-testid="evidence-no-result">Rien de lisible pour vous ne correspond.</p>}
          {results.length > 0 && (
            <ul className="max-h-44 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200" data-testid="evidence-results">
              {results.map((row) => (
                <li key={row.ref}>
                  <button type="button" disabled={has(row)} onClick={() => add(row)} className="flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jamora" data-testid="evidence-result">
                    <span className="font-medium">{row.number}</span><span className="min-w-0 truncate text-slate-600">{row.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
